import { useMemo } from 'react';

import { Box, Flex, Popover, PopoverBody, PopoverContent, PopoverTrigger, useTheme } from '@chakra-ui/react';
// import styled from '@emotion/styled';
import { endOfDay, format, isPast, parseISO } from 'date-fns';
import isFuture from 'date-fns/isFuture';
import { useGetTimezoneOffset } from 'screens/performanceReview/helpers';

import { GreenFilledCheck } from 'assets/assetsV1/iconComponents';

import { REVIEW_CYCLE_EVENTS } from 'constants/ProjectConstants';

import { Divider } from 'uiToolkitV1/Divider';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

export const BUTTON_VARIANT = {
  filled: 'filled',
  outlined: 'outlined',
  tertiary: 'tertiary',
};

// const HoverStyledBox = styled(Box)(({ theme, ...props }) => ({
//   '&:hover': {
//     '& .timeline-icon': {
//       border: `2px solid ${theme.colors.timeline['99D2F9']}`,
//       marginTop: '-2px',
//       marginLeft: '-2px',
//       boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.08)',
//     },
//     '& .timeline-circular-icon': {
//       width: '16px',
//       height: '16px',
//     },
//     '& .timeline-complete-icon': {
//       svg: {
//         width: '16px !important',
//         height: '16px !important',
//       },
//       padding: '0',
//     },
//     '& .timeline-in-progress-icon': {
//       border: `2px solid ${theme.colors.timeline.FFEFD5}`,
//     },
//     '& .timeline-diamond-icon': {
//       width: '13px',
//       height: '13px',
//       marginTop: '-1px',
//       marginLeft: '-1px',
//       boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.08)',
//     },
//   },
// }));

const NODE_STATES = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

const DiamondNodeIcon = ({ color }) => (
  <Box
    w="10px"
    h="10px"
    bg={color}
    borderRadius="2px"
    transform="rotate(45deg)"
    mt="1px"
    className="timeline-icon timeline-diamond-icon"
  />
);

const InProgressNodeIcon = () => (
  <Box
    w="12px"
    h="12px"
    borderRadius="50%"
    bg="timeline.AF5C20"
    className="timeline-icon timeline-circular-icon timeline-in-progress-icon"
  />
);
const NotStartedNodeIcon = () => (
  <Box className="timeline-icon timeline-circular-icon" w="12px" h="12px" borderRadius="50%" bg="icon.g3" />
);

const CompletedMiddleNodeIcon = () => (
  <GreenFilledCheck
    boxSize="16px"
    p="2px"
    className="timeline-icon timeline-complete-icon"
    borderRadius="50%"
    marginTop="-2px"
    marginLeft="-2px"
  />
);

const Node = ({ state = NODE_STATES.NOT_STARTED, isFirstOrLast = false }) => {
  const theme = useTheme();
  let IconComp = null;
  // just for the first and last diamond shape icon, rest have hardcoded colors
  const color =
    state === NODE_STATES.COMPLETED
      ? theme.colors.functional['44812F']
      : state === NODE_STATES.IN_PROGRESS
        ? theme.colors.functional['1565BC']
        : theme.colors.icon.g3;

  if (isFirstOrLast) {
    IconComp = DiamondNodeIcon;
  } else if (state === NODE_STATES.COMPLETED) {
    IconComp = CompletedMiddleNodeIcon;
  } else if (state === NODE_STATES.IN_PROGRESS) {
    IconComp = InProgressNodeIcon;
  } else {
    IconComp = NotStartedNodeIcon;
  }

  return (
    <Box w="16px" h="16px" display="flex" zIndex={0}>
      <IconComp color={color} />
    </Box>
  );
};

const ConnectingLine = ({ state = NODE_STATES.NOT_STARTED }) => {
  const theme = useTheme();

  const color =
    state === NODE_STATES.COMPLETED
      ? theme.colors.timeline.DBF2D3
      : state === NODE_STATES.IN_PROGRESS
        ? theme.colors.timeline.FFEFD5
        : theme.colors.timeline.CFD7DF;

  return (
    <Box
      flexGrow={1}
      h={state === NODE_STATES.NOT_STARTED ? '2px' : '12px'}
      bg={color}
      ml="-12px"
      mr="-9px"
      mt={state === NODE_STATES.NOT_STARTED ? '5px' : '0'}
    />
  );
};

const InfoTooltip = ({ title, startDateISO, endDateISO, children }) => (
  <Popover trigger="hover" placement="top-start">
    <PopoverTrigger>{children}</PopoverTrigger>
    <PopoverContent bg="transparent" border="none" boxShadow="none">
      <PopoverBody maxW="maxContent">
        <PaperWithShadow p={2} width="220px">
          <Box p="2px 8px">
            <TextBodyMedium bold>{title}</TextBodyMedium>
          </Box>

          <Divider color="neutral.F2F2F2" my={1} />
          <Flex justify="space-between" p="2px 8px">
            <TextBodyMedium>Starts on</TextBodyMedium>
            <TextBodyMedium ml={4} bold>
              {startDateISO ? format(startDateISO, 'MMM d, y') : 'NA'}
            </TextBodyMedium>
          </Flex>
          <Flex justify="space-between" p="2px 8px">
            <TextBodyMedium>Due on</TextBodyMedium>
            <TextBodyMedium ml={4} bold>
              {endDateISO ? format(endDateISO, 'MMM d, y') : 'NA'}
            </TextBodyMedium>
          </Flex>
        </PaperWithShadow>
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

export const ReviewTimeline = ({ timelineData = [] }) => {
  const formattedOffset = useGetTimezoneOffset();
  const processedTimelineData = useMemo(
    () =>
      timelineData.map((nodeData) => {
        let state = NODE_STATES.NOT_STARTED;
        const { startDate, endDate } = nodeData;
        const startDateISO = startDate ? parseISO(startDate) : null;
        const endDateISO = endDate ? endOfDay(parseISO(endDate)) : null;

        if (startDateISO && !endDateISO) {
          if (isPast(startDateISO)) {
            state = NODE_STATES.IN_PROGRESS;
          }
        } else if (endDateISO && !startDateISO) {
          if (isFuture(endDateISO)) {
            state = NODE_STATES.IN_PROGRESS;
          } else {
            state = NODE_STATES.COMPLETED;
          }
        } else if (!startDateISO && !endDateISO) {
          state = NODE_STATES.IN_PROGRESS;
        } else {
          // both startDate and endDate is there
          if (isPast(startDateISO) && isFuture(endDateISO)) {
            state = NODE_STATES.IN_PROGRESS;
          } else if (isPast(startDateISO) && isPast(endDateISO)) {
            state = NODE_STATES.COMPLETED;
          }
        }

        return {
          ...nodeData,
          state,
          startDateISO,
          endDateISO,
        };
      }),
    [timelineData]
  );

  const lastNodeProcessedData = processedTimelineData[processedTimelineData.length - 1];

  return (
    <Box>
      <Box
        bg="surface.F5F7F9"
        borderTopLeftRadius="4px"
        borderTopRightRadius="4px"
        display="flex"
        p="16px 16px 8px"
        width="100%"
      >
        {/* uncomment just to understand and see the different icons for all different possible states */}
        {/* <Node state={NODE_STATES.IN_PROGRESS} isFirstOrLast={false} />
    <Node state={NODE_STATES.COMPLETED} isFirstOrLast={false} />
    <Node state={NODE_STATES.NOT_STARTED} isFirstOrLast={false} />
    <Node state={NODE_STATES.IN_PROGRESS} isFirstOrLast={true} />
    <Node state={NODE_STATES.COMPLETED} isFirstOrLast={true} />
    <Node state={NODE_STATES.NOT_STARTED} isFirstOrLast={true} /> */}

        {processedTimelineData.map((nodeData, index) => {
          const { state } = nodeData;

          /* This is required cause the text is offset by one position from actual connecting line and point it corresponds to */
          const prevNodeData = processedTimelineData[index - 1];

          return (
            <Box display="flex" flexDir="column" flexGrow={index !== 0 ? 1 : 0} key={index}>
              <Box>
                <Box display="flex" key={nodeData.reviewStage}>
                  {index > 0 && <ConnectingLine state={state} />}
                  <Node state={state} isFirstOrLast={index === 0 || index === processedTimelineData.length - 1} />
                </Box>

                {index !== 0 && (
                  <InfoTooltip
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    title={index > 0 ? REVIEW_CYCLE_EVENTS[prevNodeData.reviewStage]?.name : null}
                    startDateISO={index > 0 ? prevNodeData.startDateISO : null}
                    endDateISO={index > 0 ? prevNodeData.endDateISO : null}
                  >
                    <Box ml="-6px">
                      <TextBodyMedium bold mx="-12px" mt={2.5}>
                        {REVIEW_CYCLE_EVENTS[prevNodeData.reviewStage]?.name}
                      </TextBodyMedium>
                      <TextBodyMedium mx="-12px" mt={1}>
                        {prevNodeData.startDateISO ? format(prevNodeData.startDateISO, 'do MMM') : 'NA'}
                        {' - '}
                        {prevNodeData.endDateISO ? format(prevNodeData.endDateISO, 'do MMM') : 'NA'}
                      </TextBodyMedium>
                    </Box>
                  </InfoTooltip>
                )}
              </Box>
            </Box>
          );
        })}
        {/* This is required cause the text is offset by one position from actual connecting line and point it corresponds to */}
        <InfoTooltip
          title={REVIEW_CYCLE_EVENTS[lastNodeProcessedData.reviewStage].name}
          startDateISO={lastNodeProcessedData.startDateISO}
          endDateISO={lastNodeProcessedData.endDateISO}
        >
          <Box display="flex" flexDir="column" flexGrow={0.2} ml="-4" mt="24px">
            <Box>
              <TextBodyMedium bold>{REVIEW_CYCLE_EVENTS[lastNodeProcessedData.reviewStage].name}</TextBodyMedium>
              <TextBodyMedium mt={1}>
                {lastNodeProcessedData.startDateISO ? format(lastNodeProcessedData.startDateISO, 'do MMM') : 'NA'}
                {' - '}
                {lastNodeProcessedData.endDateISO ? format(lastNodeProcessedData.endDateISO, 'do MMM') : 'NA'}
              </TextBodyMedium>
            </Box>
          </Box>
        </InfoTooltip>
      </Box>
      <Box
        bg="surface.F5F7F9"
        borderBottomLeftRadius="4px"
        borderBottomRightRadius="4px"
        p="8px 16px 10px"
        width="100%"
      >
        <TextLabel>{`Note: The start and end time for the reviewer submissions follows the Organisation's time zone ${formattedOffset}. Hence, all forms open at 00:00 hrs of the start date and all forms close at 23:59 hrs of the end date of that time zone.`}</TextLabel>
      </Box>
    </Box>
  );
};
